import React, { useState, useEffect, useRef } from "react";
import {
  FiMessageCircle,
  FiClock,
  FiCheckCircle,
  FiPlay,
  FiEye,
  FiArrowUpRight,
  FiAlertTriangle,
  FiBell,
  FiUserCheck,
  FiActivity,
  FiTrendingUp,
  FiChevronDown,
  FiX,
  FiMinusCircle,
  FiClock as FiClockIcon
} from "react-icons/fi";
import { useLanguage } from "../../contexts/LanguageContext";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
  LineChart,
  Line,
  CartesianGrid,
  PieChart,
  Pie,
  Legend
} from "recharts";
import { db, auth } from "../../firebase";
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  doc, 
  getDoc,
  getDocs,
  limit,
  runTransaction,
  serverTimestamp
} from "firebase/firestore";
import "./staffcss/peerdashboard.css";
import "./staffcss/contentdashboard.css";
import PeerChatForm from "./PeerChatform";

const PeerDashboard = ({ peerStatus, onPeerStatusChange, showStatusDropdown, setShowStatusDropdown, onAcceptInquiry }) => {
  const statusOptions = [
    { label: "Ready", icon: FiCheckCircle, color: "#22c55e" },
    { label: "Away", icon: FiClockIcon, color: "#f59e0b" }
  ];

  const getStatusConfig = (status) => {
    return statusOptions.find(opt => opt.label === status) || statusOptions[0];
  };
  
  // Cache for chat status counts to prevent re-querying on user changes
  const chatStatusCache = useRef({
    data: null,
    timestamp: null,
    educatorId: null
  });
  const CHAT_STATUS_CACHE_DURATION = 2 * 60 * 1000; // 2 minutes
  
  // Cache for case escalation data to prevent re-querying on user changes
  const caseEscalationCache = useRef({
    data: null,
    timestamp: null,
    educatorId: null
  });
  const CASE_ESCALATION_CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

  // Cache for daily chat summary data to prevent re-querying on user changes
  const dailySummaryCache = useRef({
    data: null,
    timestamp: null,
    educatorId: null
  });
  const DAILY_SUMMARY_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  
  // Chat categories data - fetched from Firestore
  const [chatCategoriesData, setChatCategoriesData] = useState([
    { name: "HIV Awareness", value: 0 },
    { name: "HIV Screening/Testing", value: 0 },
    { name: "Birth Control/Implant", value: 0 },
    { name: "Contraception", value: 0 },
    { name: "Family Planning", value: 0 },
    { name: "Gender-Based Violence", value: 0 },
    { name: "Menstrual Health & Hygiene", value: 0 }
  ]);
  const [loadingChatCategories, setLoadingChatCategories] = useState(true);

  const pieColors = ["#1e3a8a", "#1e40af", "#3b82f6", "#60a5fa", "#93c5fd", "#bfdbfe", "#dbeafe"];

  // Daily Chat Summary data - fetched from Firestore
  const [weeklyData, setWeeklyData] = useState([]);
  const [loadingWeeklyData, setLoadingWeeklyData] = useState(true);

  // Escalated chats data - fetched from Firestore
  const [escalatedChatsData, setEscalatedChatsData] = useState([]);
  const [loadingEscalatedChats, setLoadingEscalatedChats] = useState(true);

  // Chat status counts - fetched from Firestore
  const [chatStatusCounts, setChatStatusCounts] = useState({
    active: 0,
    standby: 0,
    inactive: 0
  });
  const [loadingChatStatus, setLoadingChatStatus] = useState(true);

  // Case escalation data - fetched from Firestore
  const [caseEscalationData, setCaseEscalationData] = useState([
    { name: "Escalated Cases", "Pending Review": 0, "Under Assessment": 0, "Resolved": 0 }
  ]);
  const [loadingCaseEscalation, setLoadingCaseEscalation] = useState(true);

  // State for assigned chats
  const [assignedChats, setAssignedChats] = useState([]);
  const [loadingAssignedChats, setLoadingAssignedChats] = useState(true);
  const [currentUserFirstName, setCurrentUserFirstName] = useState("");
  const [activeChats, setActiveChats] = useState([]);
  const [loadingActiveChats, setLoadingActiveChats] = useState(true);
  const [showAwayModal, setShowAwayModal] = useState(false);
  const [activeChatId, setActiveChatId] = useState(null);

  // Format timestamp to relative time
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Just now";
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    
    if (isNaN(date.getTime())) return "Just now";
    
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return `Just now`;
    if (diffMins < 60) return `${diffMins} mins`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''}`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''}`;
    return `${Math.floor(diffDays / 7)} week${Math.floor(diffDays / 7) > 1 ? 's' : ''} ago`;
  };

  // Handle entering chat
  const handleEnterChat = async (id, currentStatus, assignedTo) => {
    // If only one argument is passed, treat it as a chatId
    if (currentStatus === undefined) {
      setActiveChatId(id);
      if (onAcceptInquiry) {
        onAcceptInquiry(id);
      }
      return;
    }

    // Otherwise, treat as inquiry with status and assignedTo
    try {
      const educatorId = auth.currentUser?.uid;
      if (!educatorId) {
        console.error("No authenticated educator found");
        return;
      }

      // Check if current educator is the one assigned this inquiry
      if (assignedTo !== educatorId) {
        alert("You do not have permission to access this chat.");
        return;
      }

      if (currentStatus === "assigned" || currentStatus === "claimed" || currentStatus === "inactive") {
        // Enter chat for assigned/claimed/inactive inquiry
        // Find existing chat session for this inquiry
        const chatQuery = query(
          collection(db, "chats"),
          where("inquiryId", "==", id),
          where("peerEducatorId", "==", educatorId),
          limit(1)
        );
        
        let chatSnapshot = await getDocs(chatQuery);
        if (!chatSnapshot.empty) {
          const chatDoc = chatSnapshot.docs[0];
          if (onAcceptInquiry) {
            onAcceptInquiry(chatDoc.id);
          }
        } else {
          // If chat doesn't exist yet, create it now
          const inquiryRef = doc(db, "inquiries", id);
          const inquiryDoc = await getDoc(inquiryRef);
          if (!inquiryDoc.exists()) {
            throw new Error("Inquiry not found");
          }
          const inquiryData = inquiryDoc.data();

          const result = await runTransaction(db, async (transaction) => {
            const chatRef = doc(collection(db, "chats"));
            const chatId = chatRef.id;

            transaction.update(inquiryRef, {
              chatId: chatId,
              lastActivityAt: serverTimestamp(),
              userLastActivityAt: serverTimestamp()
            });

            transaction.set(chatRef, {
              inquiryId: id,
              userId: inquiryData.userId,
              peerEducatorId: educatorId,
              status: "active",
              createdAt: serverTimestamp(),
              lastMessage: inquiryData.messagePreview,
              updatedAt: serverTimestamp(),
              lastActivityAt: serverTimestamp(),
              userLastActivityAt: serverTimestamp(),
              ticketNumber: inquiryData.ticketNumber || "",
              lastMessageSender: "user",
              preferredLanguage: inquiryData.preferredLanguage,
              inquiryTopic: inquiryData.inquiryTopic,
              nextMessageOrder: messageOrder
            });

            // Add onboarding messages to messages subcollection
            const messagesRef = collection(db, "chats", chatId, "messages");
            const baseTimestamp = serverTimestamp();
            let messageOrder = 0;
            
            // 1. Hi there message
            const msg1Ref = doc(messagesRef);
            transaction.set(msg1Ref, {
              senderId: "system",
              senderRole: "system",
              text: "Hi there! You're about to chat with an AuraVit Peer Educator.",
              createdAt: baseTimestamp,
              order: messageOrder++
            });

            // 2. I understand (user message)
            const msg2Ref = doc(messagesRef);
            transaction.set(msg2Ref, {
              senderId: inquiryData.userId,
              senderRole: "user",
              text: "I Understand",
              createdAt: serverTimestamp(),
              order: messageOrder++
            });

            // 3. Choose preferred language
            const msg3Ref = doc(messagesRef);
            transaction.set(msg3Ref, {
              senderId: "system",
              senderRole: "system",
              text: "Great! Before we continue, please choose your preferred language so we can match you with a peer educator who you can communicate comfortably with.",
              createdAt: serverTimestamp(),
              order: messageOrder++
            });

            // 4. User's preferred language
            if (inquiryData.preferredLanguage) {
              const langLabel = inquiryData.preferredLanguage === "english" ? "English" : 
                               inquiryData.preferredLanguage === "tagalog" ? "Tagalog" : 
                               inquiryData.preferredLanguage === "bisaya" ? "Bisaya" : 
                               inquiryData.preferredLanguage;
              const msg4Ref = doc(messagesRef);
              transaction.set(msg4Ref, {
                senderId: inquiryData.userId,
                senderRole: "user",
                text: langLabel,
                createdAt: serverTimestamp(),
                order: messageOrder++
              });
            }

            // 5. Choose topic
            const msg5Ref = doc(messagesRef);
            transaction.set(msg5Ref, {
              senderId: "system",
              senderRole: "system",
              text: "Excellent! To help us better assist you, what topic is your inquiry about?",
              createdAt: serverTimestamp(),
              order: messageOrder++
            });

            // 6. User's inquiry topic
            if (inquiryData.inquiryTopic) {
              const msg6Ref = doc(messagesRef);
              transaction.set(msg6Ref, {
                senderId: inquiryData.userId,
                senderRole: "user",
                text: inquiryData.inquiryTopic,
                createdAt: serverTimestamp(),
                order: messageOrder++
              });
            }

            // 7. Thank you for sharing
            const msg7Ref = doc(messagesRef);
            transaction.set(msg7Ref, {
              senderId: "system",
              senderRole: "system",
              text: "Thank you for sharing. You can now send your question or message, and our Peer Educator will be ready to assist you.",
              createdAt: serverTimestamp(),
              order: messageOrder++
            });

            // 8. User's message preview (inquiry)
            const msg8Ref = doc(messagesRef);
            transaction.set(msg8Ref, {
              senderId: inquiryData.userId,
              senderRole: "user",
              text: inquiryData.messagePreview,
              createdAt: serverTimestamp(),
              order: messageOrder++
            });

            // 9. Ticket number message
            const msg9Ref = doc(messagesRef);
            transaction.set(msg9Ref, {
              senderId: "system",
              senderRole: "system",
              text: `Your inquiry has been submitted! Ticket number: ${inquiryData.ticketNumber} Waiting for a peer educator...`,
              createdAt: serverTimestamp(),
              order: messageOrder++
            });

            return { chatId };
          });

          if (onAcceptInquiry) {
            onAcceptInquiry(result.chatId);
          }
        }
      } else if (currentStatus === "resolved") {
        // View resolved chat in read-only mode

        
        const chatQuery = query(
          collection(db, "chats"),
          where("inquiryId", "==", id),
          where("peerEducatorId", "==", educatorId),
          limit(1)
        );
        
        const chatSnapshot = await getDocs(chatQuery);
        if (!chatSnapshot.empty) {
          const chatDoc = chatSnapshot.docs[0];
          if (onAcceptInquiry) {
            onAcceptInquiry(chatDoc.id);
          }
        } else {
          alert("Chat session not found for this inquiry.");
        }
      }
    } catch (error) {
      console.error("Failed to enter chat:", error);
      alert("Failed to enter chat. Please try again.");
    }
  };

  // Fetch user first name
  useEffect(() => {
    const user = auth.currentUser;
    if (user) {
      const fetchUserDoc = async () => {
        try {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const fullName = userData.fullName || userData.name || "";
            const firstName = fullName.trim().split(" ")[0];
            setCurrentUserFirstName(firstName);
          } else if (user.displayName) {
            const firstName = user.displayName.trim().split(" ")[0];
            setCurrentUserFirstName(firstName);
          }
        } catch (err) {
          console.error("Error fetching user doc:", err);
        }
      };
      fetchUserDoc();
    }
  }, [auth.currentUser]);

  // Fetch assigned chats (3 oldest)
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setLoadingAssignedChats(false);
      return;
    }

    setLoadingAssignedChats(true);
    const educatorId = user.uid;

    // Query for assigned inquiries - OPTIMIZED
    const q = query(
      collection(db, "inquiries"),
      where("assignedTo", "==", educatorId),
      where("status", "==", "assigned"),
      orderBy("createdAt", "asc"), // Oldest first
      limit(3)
    );

    let mounted = true;

    const unsubscribe = onSnapshot(q,
      async (snapshot) => {
        if (!mounted) return;
        
        const chatsData = [];
        for (const docSnap of snapshot.docs) {
          const data = docSnap.data();
          chatsData.push({
            id: docSnap.id,
            ticketNumber: data.ticketNumber,
            topic: data.messagePreview,
            lastActivityAt: data.lastActivityAt || data.createdAt,
            assignedTo: data.assignedTo,
            status: data.status
          });
        }
        setAssignedChats(chatsData);
        setLoadingAssignedChats(false);
      },
      (error) => {
        console.error("Error fetching assigned chats:", error);
        setLoadingAssignedChats(false);
      }
    );

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [auth.currentUser?.uid]); // Only recreate when user ID changes, not on every auth state change

  // Fetch active chats from chats collection - OPTIMIZED
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setActiveChats([]);
      return;
    }

    const educatorId = user.uid;

    const qActiveChats = query(
      collection(db, "chats"),
      where("peerEducatorId", "==", educatorId),
      where("status", "==", "active"),
      orderBy("createdAt", "desc"),
      limit(3)
    );

    let mounted = true;

    const unsubscribe = onSnapshot(qActiveChats,
      (snapshot) => {
        if (!mounted) return;
        
        const activeChatsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setActiveChats(activeChatsData);
      },
      (error) => {
        console.error("Error fetching active chats:", error);
      }
    );

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [auth.currentUser?.uid]); // Only recreate when user ID changes, not on every auth state change

  // Fetch chat status counts from Firestore
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setChatStatusCounts({ active: 0, standby: 0, inactive: 0 });
      setLoadingChatStatus(false);
      return;
    }

    const educatorId = user.uid;
    const now = Date.now();
    const cache = chatStatusCache.current;
    
    // Check if we have valid cached data for this educator
    if (cache.data && 
        cache.educatorId === educatorId && 
        cache.timestamp && 
        (now - cache.timestamp) < CHAT_STATUS_CACHE_DURATION) {
      setChatStatusCounts(cache.data);
      setLoadingChatStatus(false);
      return;
    }

    // Set up real-time listener with single query and client-side filtering - OPTIMIZED
    const chatsQuery = query(
      collection(db, "chats"),
      where("peerEducatorId", "==", educatorId),
      limit(100) // Add limit to prevent reading all chats
    );
    
    let mounted = true;
    
    const unsubscribe = onSnapshot(chatsQuery, (snapshot) => {
      if (!mounted) return;
      
      let activeCount = 0;
      let standbyCount = 0;
      let inactiveCount = 0;

      snapshot.docs.forEach(doc => {
        const status = doc.data().status;
        if (status === "active") activeCount++;
        else if (status === "standby") standbyCount++;
        else if (status === "inactive") inactiveCount++;
      });

      const newCounts = { active: activeCount, standby: standbyCount, inactive: inactiveCount };
      setChatStatusCounts(newCounts);
      setLoadingChatStatus(false);
      
      // Update cache
      chatStatusCache.current = {
        data: newCounts,
        timestamp: Date.now(),
        educatorId: educatorId
      };
    }, (error) => {
      console.error("Error in chat status listener:", error);
      setLoadingChatStatus(false);
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [auth.currentUser?.uid]); // Only recreate when user ID changes, not on every auth state change

  // Fetch chat categories for today from Firestore
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setChatCategoriesData([
        { name: "HIV Awareness", value: 0 },
        { name: "HIV Screening/Testing", value: 0 },
        { name: "Birth Control/Implant", value: 0 },
        { name: "Contraception", value: 0 },
        { name: "Family Planning", value: 0 },
        { name: "Gender-Based Violence", value: 0 },
        { name: "Menstrual Health & Hygiene", value: 0 }
      ]);
      setLoadingChatCategories(false);
      return;
    }

    const educatorId = user.uid;

    const fetchChatCategories = async () => {
      try {
        setLoadingChatCategories(true);
        // Get today's date range (start and end of day)
        const today = new Date();
        const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
        const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);

        // Convert to Firestore timestamps
        const startTimestamp = new Date(startOfDay);
        const endTimestamp = new Date(endOfDay);

        // Query chats for today, filtered by peer educator ID
        const chatsQuery = query(
          collection(db, "chats"),
          where("peerEducatorId", "==", educatorId),
          where("createdAt", ">=", startTimestamp),
          where("createdAt", "<=", endTimestamp)
        );

        const chatsSnapshot = await getDocs(chatsQuery);

        // Count chats per inquiryTopic
        const topicCounts = {
          "HIV Awareness": 0,
          "HIV Screening/Testing": 0,
          "Birth Control/Implant": 0,
          "Contraception": 0,
          "Family Planning": 0,
          "Gender-Based Violence": 0,
          "Menstrual Health & Hygiene": 0
        };

        chatsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const topic = data.inquiryTopic;
          
          // Match topic to our categories (case-insensitive)
          if (topic) {
            const topicLower = topic.toLowerCase();
            if (topicLower.includes("hiv") && (topicLower.includes("aware") || topicLower.includes("education") || topicLower.includes("info"))) {
              topicCounts["HIV Awareness"]++;
            } else if (topicLower.includes("hiv") && (topicLower.includes("screen") || topicLower.includes("test"))) {
              topicCounts["HIV Screening/Testing"]++;
            } else if (topicLower.includes("birth control") || topicLower.includes("implant")) {
              topicCounts["Birth Control/Implant"]++;
            } else if (topicLower.includes("contraception") || topicLower.includes("condom") || topicLower.includes("pill")) {
              topicCounts["Contraception"]++;
            } else if (topicLower.includes("family") && topicLower.includes("plan")) {
              topicCounts["Family Planning"]++;
            } else if (topicLower.includes("violence") || topicLower.includes("abuse") || topicLower.includes("gbv")) {
              topicCounts["Gender-Based Violence"]++;
            } else if (topicLower.includes("menstrual") || topicLower.includes("period") || topicLower.includes("hygiene")) {
              topicCounts["Menstrual Health & Hygiene"]++;
            }
          }
        });

        // Update state with counts
        const finalData = [
          { name: "HIV Awareness", value: topicCounts["HIV Awareness"] },
          { name: "HIV Screening/Testing", value: topicCounts["HIV Screening/Testing"] },
          { name: "Birth Control/Implant", value: topicCounts["Birth Control/Implant"] },
          { name: "Contraception", value: topicCounts["Contraception"] },
          { name: "Family Planning", value: topicCounts["Family Planning"] },
          { name: "Gender-Based Violence", value: topicCounts["Gender-Based Violence"] },
          { name: "Menstrual Health & Hygiene", value: topicCounts["Menstrual Health & Hygiene"] }
        ];
        setChatCategoriesData(finalData);
        setLoadingChatCategories(false);
      } catch (error) {
        console.error("Error fetching chat categories:", error);
        setLoadingChatCategories(false);
      }
    };

    fetchChatCategories();
  }, [auth.currentUser]);

  // Fetch case escalation counts from escalatedChats collection with single query
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setCaseEscalationData([
        { name: "Escalated Cases", "Pending Review": 0, "Under Assessment": 0, "Resolved": 0 }
      ]);
      setLoadingCaseEscalation(false);
      return;
    }

    const educatorId = user.uid;
    const now = Date.now();
    const cache = caseEscalationCache.current;
    
    // Check if we have valid cached data for this educator
    if (cache.data && 
        cache.educatorId === educatorId && 
        cache.timestamp && 
        (now - cache.timestamp) < CASE_ESCALATION_CACHE_DURATION) {
      setCaseEscalationData(cache.data);
      setLoadingCaseEscalation(false);
      return;
    }

    const fetchCaseEscalationCounts = async () => {
      try {
        setLoadingCaseEscalation(true);
        // Single query with client-side filtering - OPTIMIZED
        const escalationQuery = query(
          collection(db, "escalatedChats"),
          where("peerEducatorId", "==", educatorId),
          limit(50) // Add limit to prevent reading all escalated chats
        );
        const escalationSnapshot = await getDocs(escalationQuery);
        
        // Client-side filtering for different statuses
        let pendingCount = 0;
        let underAssessmentCount = 0;
        let resolvedCount = 0;
        
        escalationSnapshot.docs.forEach(doc => {
          const status = doc.data().escalationStatus;
          if (status === "pending-review") {
            pendingCount++;
          } else if (status === "under-assessment") {
            underAssessmentCount++;
          } else if (status === "resolved") {
            resolvedCount++;
          }
        });

        const finalData = [
          { name: "Escalated Cases", "Pending Review": pendingCount, "Under Assessment": underAssessmentCount, "Resolved": resolvedCount }
        ];
        setCaseEscalationData(finalData);
        setLoadingCaseEscalation(false);
        
        // Update cache
        caseEscalationCache.current = {
          data: finalData,
          timestamp: now,
          educatorId: educatorId
        };
      } catch (error) {
        console.error("Error fetching case escalation counts:", error);
        setLoadingCaseEscalation(false);
      }
    };

    fetchCaseEscalationCounts();
  }, [auth.currentUser]);

  // Fetch escalated chats from escalatedchats collection
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setEscalatedChatsData([]);
      setLoadingEscalatedChats(false);
      return;
    }

    const educatorId = user.uid;

    const fetchEscalatedChats = async () => {
      try {
        setLoadingEscalatedChats(true);
        const escalatedQuery = query(
          collection(db, "escalatedChats"),
          where("peerEducatorId", "==", educatorId),
          orderBy("createdAt", "desc"),
          limit(3)
        );

        const escalatedSnapshot = await getDocs(escalatedQuery);

        const escalatedChats = escalatedSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        setEscalatedChatsData(escalatedChats);
        setLoadingEscalatedChats(false);
      } catch (error) {
        console.error("Error fetching escalated chats:", error);
        setLoadingEscalatedChats(false);
      }
    };

    fetchEscalatedChats();
  }, [auth.currentUser]);

  // Fetch daily chat summary (last 7 days) from chats collection
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      setWeeklyData([]);
      setLoadingWeeklyData(false);
      return;
    }

    const educatorId = user.uid;
    const now = Date.now();
    const cache = dailySummaryCache.current;

    // Check if we have valid cached data for this educator
    if (cache.data &&
        cache.educatorId === educatorId &&
        cache.timestamp &&
        (now - cache.timestamp) < DAILY_SUMMARY_CACHE_DURATION) {
      setWeeklyData(cache.data);
      setLoadingWeeklyData(false);
      return;
    }

    const fetchDailySummary = async () => {
      try {
        setLoadingWeeklyData(true);
        // Calculate date range for last 7 days
        const today = new Date();
        const sevenDaysAgo = new Date(today);
        sevenDaysAgo.setDate(today.getDate() - 6);
        sevenDaysAgo.setHours(0, 0, 0, 0);
        today.setHours(23, 59, 59, 999);

        // Query chats for the last 7 days
        const chatsQuery = query(
          collection(db, "chats"),
          where("peerEducatorId", "==", educatorId),
          where("createdAt", ">=", sevenDaysAgo),
          where("createdAt", "<=", today),
          limit(500)
        );

        const chatsSnapshot = await getDocs(chatsQuery);

        // Initialize counts for each day
        const dailyCounts = {};
        for (let i = 6; i >= 0; i--) {
          const date = new Date(today);
          date.setDate(today.getDate() - i);
          const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          dailyCounts[dateStr] = { assigned: 0, resolved: 0 };
        }

        // Count chats by date
        chatsSnapshot.docs.forEach(doc => {
          const data = doc.data();
          const createdAt = data.createdAt?.toDate ? data.createdAt.toDate() : new Date(data.createdAt?.seconds * 1000);
          const dateStr = createdAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

          if (dailyCounts[dateStr]) {
            dailyCounts[dateStr].assigned++;
          }

          // Check if chat is resolved - use status field
          if (data.status === "resolved") {
            // Try to use resolvedAt timestamp if available, otherwise use updatedAt
            const resolvedAt = data.resolvedAt?.toDate ? data.resolvedAt.toDate() :
                              data.updatedAt?.toDate ? data.updatedAt.toDate() :
                              new Date(data.updatedAt?.seconds * 1000);
            const resolvedDateStr = resolvedAt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

            if (dailyCounts[resolvedDateStr]) {
              dailyCounts[resolvedDateStr].resolved++;
            }
          }
        });

        // Convert to array format for the chart
        const finalData = Object.keys(dailyCounts).map(dateStr => ({
          name: dateStr,
          assigned: dailyCounts[dateStr].assigned,
          resolved: dailyCounts[dateStr].resolved
        }));

        setWeeklyData(finalData);
        setLoadingWeeklyData(false);

        // Update cache
        dailySummaryCache.current = {
          data: finalData,
          timestamp: now,
          educatorId: educatorId
        };
      } catch (error) {
        console.error("Error fetching daily chat summary:", error);
        setLoadingWeeklyData(false);
      }
    };

    fetchDailySummary();
  }, [auth.currentUser]);

  // Handle status change with active chats check
  const handleStatusChange = (newStatus) => {
    if (newStatus === "Away") {
      if (activeChats.length > 0) {
        setShowAwayModal(true);
        return;
      }
    }
    onPeerStatusChange(newStatus);
  };



  // Handle back to dashboard
  const handleBackToDashboard = () => {
    setActiveChatId(null);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip" style={{ backgroundColor: "white", padding: "10px", border: "1px solid #ccc", borderRadius: "4px" }}>
          <p className="label" style={{ margin: 0, marginBottom: "5px" }}>{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ margin: 0, color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="pd-main">
      {activeChatId ? (
        <PeerChatForm chatId={activeChatId} onBackToQueue={handleBackToDashboard} />
      ) : (
        <>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 className="pd-welcome">Welcome Back, {currentUserFirstName || "Peer Educator"}!</h1>
        <div className="pe-status-dropdown">
          <button 
            className="pe-status-toggle-btn"
            onClick={() => setShowStatusDropdown(!showStatusDropdown)}
          >
            {(() => {
              const config = getStatusConfig(peerStatus);
              const Icon = config.icon;
              return (
                <>
                  <Icon className="pe-status-icon" style={{ color: config.color }} />
                  <span className="pe-status-label">{peerStatus}</span>
                  <FiChevronDown className={`pe-status-chevron ${showStatusDropdown ? 'open' : ''}`} />
                </>
              );
            })()}
          </button>
          {showStatusDropdown && (
            <div className="pe-status-dropdown-menu">
              {statusOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.label}
                    className={`pe-status-option ${peerStatus === option.label ? 'active' : ''}`}
                    onClick={() => handleStatusChange(option.label)}
                  >
                    <Icon className="pe-status-option-icon" style={{ color: option.color }} />
                    <span>{option.label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* New Two Column Layout for Weekly Summary and Chats Status */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "24px", marginBottom: "24px" }}>
        {/* Left: Weekly Summary Line Graph */}
        <div className="cc-analytics">
          <header className="cc-section-header">Daily Chat Summary</header>
          <div style={{ padding: "16px", height: "calc(100% - 56px)", display: "flex", flexDirection: "column" }}>
            {loadingWeeklyData ? (
              <div className="skeleton-chart">
                <div className="skeleton skeleton-header"></div>
                <div className="skeleton skeleton-subheader"></div>
                <div className="skeleton skeleton-content"></div>
              </div>
            ) : (
              <>
                <div style={{ flex: 1, minHeight: 0 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={weeklyData} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
                      <CartesianGrid stroke="#e0e7ff" strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fill: "#1e40af" }} />
                      <YAxis tick={{ fill: "#1e40af" }} allowDecimals={false} />
                      <RechartsTooltip content={<CustomTooltip />} />
                      <Line type="monotone" dataKey="assigned" name="Assigned" stroke="#3b82f6" strokeWidth={3} dot={{ r: 4, fill: "#3b82f6" }} />
                      <Line type="monotone" dataKey="resolved" name="Resolved" stroke="#10b981" strokeWidth={3} dot={{ r: 4, fill: "#10b981" }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                {/* Legend */}
                <div style={{ display: "flex", justifyContent: "center", gap: "20px", marginTop: "15px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <div style={{ width: "12px", height: "12px", backgroundColor: "#3b82f6" }}></div>
                    <span style={{ color: "#1e40af" }}>Assigned</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <div style={{ width: "12px", height: "12px", backgroundColor: "#10b981" }}></div>
                    <span style={{ color: "#1e40af" }}>Resolved</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Right: Chats Status Report */}
        <div className="cc-analytics">
          <header className="cc-section-header">Chats Status Report</header>
          <div style={{ 
            padding: "16px", 
            display: "grid", 
            gridTemplateColumns: "1fr 1fr", 
            gap: "16px",
            height: "calc(100% - 56px)"
          }} className="chat-status-grid">
            {loadingChatStatus ? (
              <>
                {/* Left: Active (Large Square) */}
                <div className="skeleton skeleton-card"></div>
                {/* Right: Standby + Inactive */}
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <div className="skeleton skeleton-card"></div>
                  <div className="skeleton skeleton-card"></div>
                </div>
              </>
            ) : (
              <>
                {/* Left: Active (Large Square) */}
                <div style={{ 
                  background: "linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)",
                  borderRadius: "12px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  padding: "20px",
                  aspectRatio: "3/4",
                  width: "100%",
                  height: "auto"
                }} className="chat-status-card">
                  <span style={{ 
                    fontSize: "200px", 
                    fontWeight: "800", 
                    color: "#ffffff",
                    textShadow: "0 0 30px rgba(255,255,255,0.5), 0 4px 20px rgba(0,0,0,0.2)",
                    letterSpacing: "-2px",
                    lineHeight: "1",
                    flex: "0 0 auto",
                    alignSelf: "flex-start"
                  }} className="chat-count">{chatStatusCounts.active}</span>
                  <span style={{ 
                    fontSize: "40px", 
                    fontWeight: "600", 
                    color: "#ffffff",
                    opacity: "0.9",
                    alignSelf: "flex-end"
                  }} className="chat-label">Active</span>
                </div>

                {/* Right: Standby + Inactive */}
                <div style={{ 
                  display: "flex", 
                  flexDirection: "column", 
                  gap: "16px", 
                  height: "100%"
                }} className="chat-status-right">
                  {/* Standby */}
                  <div style={{ 
                    background: "linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%)",
                    borderRadius: "12px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    padding: "20px",
                    flex: 1
                  }} className="chat-status-card">
                    <span style={{ 
                      fontSize: "80px", 
                      fontWeight: "800", 
                      color: "#ffffff",
                      textShadow: "0 0 30px rgba(255,255,255,0.5), 0 4px 20px rgba(0,0,0,0.2)",
                      letterSpacing: "-2px",
                      lineHeight: "1",
                      flex: "0 0 auto",
                      alignSelf: "flex-start"
                    }} className="chat-count">{chatStatusCounts.standby}</span>
                    <span style={{ 
                      fontSize: "20px", 
                      fontWeight: "600", 
                      color: "#ffffff",
                      opacity: "0.9",
                      alignSelf: "flex-end"
                    }} className="chat-label">Standby</span>
                  </div>

                  {/* Inactive */}
                  <div style={{ 
                    background: "linear-gradient(135deg, #93c5fd 0%, #60a5fa 100%)",
                    borderRadius: "12px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    padding: "20px",
                    flex: 1
                  }} className="chat-status-card">
                    <span style={{ 
                      fontSize: "80px", 
                      fontWeight: "800", 
                      color: "#ffffff",
                      textShadow: "0 0 30px rgba(255,255,255,0.5), 0 4px 20px rgba(0,0,0,0.2)",
                      letterSpacing: "-2px",
                      lineHeight: "1",
                      flex: "0 0 auto",
                      alignSelf: "flex-start"
                    }} className="chat-count">{chatStatusCounts.inactive}</span>
                    <span style={{ 
                      fontSize: "20px", 
                      fontWeight: "600", 
                      color: "#ffffff",
                      opacity: "0.9",
                      alignSelf: "flex-end"
                    }} className="chat-label">Inactive</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Original Two Column Layout */}
      <div className="pd-two-columns">
        {/* Left Column */}
        <div className="pd-column">
          {/* Case Escalation Tracker Bar Graph */}
          <div className="cc-analytics" style={{ marginBottom: "24px" }}>
            <header className="cc-section-header">Case Escalation Tracker</header>
            <div className="cc-chart-container" style={{ height: "420px", padding: "16px" }}>
              {loadingCaseEscalation ? (
                <div className="skeleton-chart">
                  <div className="skeleton skeleton-header"></div>
                  <div className="skeleton skeleton-subheader"></div>
                  <div className="skeleton-bars-horizontal">
                    {[1, 2, 3].map((index) => (
                      <div key={index} className="skeleton-bar-row">
                        <div className="skeleton skeleton-bar-label"></div>
                        <div 
                          className="skeleton skeleton-bar-fill" 
                          style={{ width: `${90 - (index * 15)}%` }}
                        ></div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <div style={{ height: "320px" }}>
                    <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      layout="vertical"
                      data={caseEscalationData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                      barSize={40}
                      barGap={10}
                    >
                      <CartesianGrid stroke="#e0e7ff" strokeDasharray="3 3" />
                      <XAxis type="number" tick={{ fill: "#1e40af" }} allowDecimals={false} />
                      <YAxis dataKey="name" type="category" tick={{ fill: "#1e40af" }} />
                      <RechartsTooltip />
                      <Bar dataKey="Pending Review" name="Pending Review" fill="#93c5fd" />
                      <Bar dataKey="Under Assessment" name="Under Assessment" fill="#3b82f6" />
                      <Bar dataKey="Resolved" name="Resolved" fill="#1e40af" />
                    </BarChart>
                  </ResponsiveContainer>
                  </div>
                  {/* Legend */}
                  <div style={{ display: "flex", justifyContent: "flex-start", gap: "20px", marginTop: "15px", paddingLeft: "16px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <div style={{ width: "12px", height: "12px", backgroundColor: "#93c5fd" }}></div>
                      <span style={{ color: "#1e40af" }}>Pending Review</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <div style={{ width: "12px", height: "12px", backgroundColor: "#3b82f6" }}></div>
                      <span style={{ color: "#1e40af" }}>Under Assessment</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                      <div style={{ width: "12px", height: "12px", backgroundColor: "#1e40af" }}></div>
                      <span style={{ color: "#1e40af" }}>Resolved</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Assigned Chats */}
          <div className="cc-analytics">
            <header className="cc-section-header">Assigned Chats</header>
            <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
              {loadingAssignedChats ? (
                [1, 2, 3].map((index) => (
                  <div
                    key={index}
                    style={{
                      padding: "16px",
                      borderRadius: "12px",
                      background: "#f3f4f6",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                      alignItems: "flex-start"
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                      <div style={{ textAlign: "left" }}>
                        <div className="skeleton skeleton-header"></div>
                        <div className="skeleton skeleton-subheader"></div>
                        <div className="skeleton" style={{ width: "200px", height: "12px", marginTop: "4px" }}></div>
                      </div>
                      <div className="skeleton" style={{ width: "80px", height: "32px", borderRadius: "8px", marginLeft: "16px" }}></div>
                    </div>
                  </div>
                ))
              ) : (
                <>
                  {activeChats.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "24px", color: "#64748b" }}>
                      No active chats
                    </div>
                  ) : (
                    activeChats.map((chat) => (
                      <div
                        key={chat.id}
                        style={{
                          padding: "16px",
                          borderRadius: "12px",
                          background: "linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(30,58,138,0.1) 100%)",
                          border: "1px solid rgba(59,130,246,0.2)",
                          display: "flex",
                          flexDirection: "column",
                          gap: "8px",
                          alignItems: "flex-start"
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                          <div style={{ textAlign: "left" }}>
                            <div style={{ fontSize: "14px", fontWeight: "700", color: "#1e3a8a" }}>
                              {chat.inquiryTopic || chat.category}
                            </div>
                            <div style={{ fontSize: "13px", color: "#64748b", marginTop: "4px" }}>
                              Ticket: {chat.ticketNumber}
                            </div>
                            <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "4px" }}>
                              {chat.lastMessage || chat.messagePreview}
                            </div>
                          </div>
                          <button
                            onClick={() => handleEnterChat(chat.id)}
                            style={{
                              padding: "8px 16px",
                              background: "linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)",
                              color: "white",
                              border: "none",
                              borderRadius: "8px",
                              fontSize: "13px",
                              fontWeight: "600",
                              cursor: "pointer",
                              flexShrink: 0,
                              marginLeft: "16px",
                              transition: "all 0.2s ease",
                              boxShadow: "0 2px 4px rgba(59, 130, 246, 0.2)"
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.background = "linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%)";
                              e.target.style.transform = "translateY(-1px)";
                              e.target.style.boxShadow = "0 4px 8px rgba(59, 130, 246, 0.3)";
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.background = "linear-gradient(135deg, #3b82f6 0%, #1e40af 100%)";
                              e.target.style.transform = "translateY(0)";
                              e.target.style.boxShadow = "0 2px 4px rgba(59, 130, 246, 0.2)";
                            }}
                          >
                            Enter Chat
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="pd-column">
          {/* Chat Categories Today Pie Chart */}
          <div className="cc-analytics" style={{ marginBottom: "24px" }}>
            <header className="cc-section-header">Chat Categories Today</header>
            <div className="pd-bar-chart-container chat-categories-grid" style={{ display: "grid", gridTemplateColumns: "1fr 3fr", gap: "16px", height: "380px" }}>
              {loadingChatCategories ? (
                <>
                  {/* Left: Legend Skeleton */}
                  <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: "8px", alignItems: "flex-start", paddingLeft: "8px" }}>
                    {[1, 2, 3, 4, 5, 6, 7].map((index) => (
                      <div key={index} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div className="skeleton" style={{ width: "12px", height: "12px", borderRadius: "2px", flexShrink: 0 }}></div>
                        <div className="skeleton" style={{ width: "120px", height: "14px" }}></div>
                      </div>
                    ))}
                  </div>
                  {/* Right: Pie Chart Skeleton */}
                  <div className="skeleton skeleton-pie"></div>
                </>
              ) : (
                <>
                  {/* Left: Legend */}
                  <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", gap: "8px", alignItems: "flex-start", paddingLeft: "8px" }} className="chat-categories-legend">
                    {chatCategoriesData.map((entry, index) => (
                      <div key={index} style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <div style={{ width: "12px", height: "12px", backgroundColor: pieColors[index % pieColors.length], borderRadius: "2px", flexShrink: 0 }}></div>
                        <span style={{ color: "#1e40af", fontSize: "14px", textAlign: "left" }}>{entry.name}</span>
                      </div>
                    ))}
                  </div>
                  {/* Right: Pie Chart */}
                  {(() => {
                    const allZero = chatCategoriesData.every(entry => entry.value === 0);
                    if (allZero) {
                      return (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b", fontSize: "14px" }}>
                          No chat data for today yet
                        </div>
                      );
                    }
                    return (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={chatCategoriesData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            outerRadius={120}
                            fill="#8884d8"
                            dataKey="value"
                            nameKey="name"
                          >
                            {chatCategoriesData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                            ))}
                          </Pie>
                          <RechartsTooltip
                            formatter={(value, name) => [`${value}`, name]}
                            labelFormatter={(name) => name}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    );
                  })()}
                </>
              )}
            </div>
          </div>

          {/* Escalated Chats */}
          <div className="cc-analytics">
            <header className="cc-section-header">Escalated Chats</header>
            <div style={{ padding: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
              {loadingEscalatedChats ? (
                [1, 2, 3].map((index) => (
                  <div
                    key={index}
                    style={{
                      padding: "16px",
                      borderRadius: "12px",
                      background: "#f3f4f6",
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px"
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div style={{ textAlign: "left" }}>
                        <div className="skeleton skeleton-header"></div>
                        <div className="skeleton skeleton-subheader"></div>
                      </div>
                      <div className="skeleton" style={{ width: "100px", height: "24px", borderRadius: "999px" }}></div>
                    </div>
                    <div className="skeleton" style={{ width: "200px", height: "14px" }}></div>
                  </div>
                ))
              ) : (
                <>
                  {escalatedChatsData.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "24px", color: "#64748b" }}>
                      No escalated chats
                    </div>
                  ) : (
                    escalatedChatsData.map((chat) => {
                      let statusColor, statusBg, cardBg;
                      const status = chat.escalationStatus || chat.status;
                      if (status === "resolved") {
                        statusColor = "#059669";
                        statusBg = "rgba(16,185,129,0.2)";
                        cardBg = "linear-gradient(135deg, rgba(16,185,129,0.1) 0%, rgba(21,128,61,0.1) 100%)";
                      } else if (status === "under-assessment") {
                        statusColor = "#d97706";
                        statusBg = "rgba(245,158,11,0.2)";
                        cardBg = "linear-gradient(135deg, rgba(245,158,11,0.1) 0%, rgba(217,119,6,0.1) 100%)";
                      } else {
                        statusColor = "#1d4ed8";
                        statusBg = "rgba(59,130,246,0.2)";
                        cardBg = "linear-gradient(135deg, rgba(59,130,246,0.1) 0%, rgba(30,58,138,0.1) 100%)";
                      }
                      const displayStatus = status === "pending-review" ? "Pending Review" : 
                                           status === "under-assessment" ? "Under Assessment" :
                                           status === "resolved" ? "Resolved" : status;
                      return (
                        <div
                          key={chat.id}
                          onClick={() => {
                            if (chat.chatId) {
                              setActiveChatId(chat.chatId);
                              if (onAcceptInquiry) {
                                onAcceptInquiry(chat.chatId);
                              }
                            }
                          }}
                          style={{
                            padding: "16px",
                            borderRadius: "12px",
                            background: cardBg,
                            border: "1px solid rgba(59,130,246,0.2)",
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            cursor: "pointer",
                            transition: "all 0.2s ease"
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.transform = "translateY(-2px)";
                            e.target.style.boxShadow = "0 4px 12px rgba(59, 130, 246, 0.3)";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = "none";
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                            <div style={{ textAlign: "left" }}>
                              <div style={{ fontSize: "14px", fontWeight: "700", color: "#1e3a8a" }}>
                                {chat.escalationTopic || chat.topic}
                              </div>
                              <div style={{ fontSize: "13px", color: "#64748b", marginTop: "4px" }}>
                                Ticket: {chat.ticketNumber}
                              </div>
                            </div>
                            <span
                              style={{
                                padding: "4px 12px",
                                borderRadius: "999px",
                                fontSize: "12px",
                                fontWeight: "600",
                                color: statusColor,
                                background: statusBg
                              }}
                            >
                              {displayStatus}
                            </span>
                          </div>
                          <div style={{ fontSize: "13px", color: "#64748b", textAlign: "left" }}>
                            Reason: {chat.escalationReason}
                          </div>
                        </div>
                      );
                    })
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
        </>
      )}

      {/* Away Status Warning Modal */}
      {showAwayModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(15, 23, 42, 0.85)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            overflow: 'hidden',
            maxWidth: '450px',
            width: '100%',
            boxShadow: '0 20px 40px rgba(15, 23, 42, 0.3)'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              padding: '20px 24px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{
                margin: 0,
                color: '#fff',
                fontSize: '18px',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <FiAlertTriangle style={{ fontSize: '20px' }} /> Cannot Go Away
              </h3>
              <button
                type="button"
                onClick={() => setShowAwayModal(false)}
                style={{
                  background: 'rgba(255,255,255,0.15)',
                  border: '1px solid rgba(255,255,255,0.25)',
                  borderRadius: '10px',
                  color: '#fff',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <FiX style={{ fontSize: '18px' }} />
              </button>
            </div>

            <div style={{ padding: '28px' }}>
              <p style={{
                margin: '0 0 24px 0',
                fontSize: '15px',
                lineHeight: '1.6',
                color: '#374151'
              }}>
                You cannot switch to "Away" status while you have active chats. Please complete or resolve your active chats before changing your status.
              </p>

              <button
                onClick={() => setShowAwayModal(false)}
                style={{
                  width: '100%',
                  padding: '12px 24px',
                  background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  boxShadow: '0 4px 6px rgba(30, 58, 138, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #1e40af 0%, #60a5fa 100%)';
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 6px 12px rgba(30, 58, 138, 0.3)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)';
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 6px rgba(30, 58, 138, 0.2)';
                }}
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeerDashboard;
